import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { tenantWidgetAccessService } from "../services/tenantWidgetAccessService";
import { tenantWidgetAccessDisplayId } from "./TenantWidgetAccessCommonStore";
import { TenantWidgetAccess } from "../../../api/graphql";
import { tenantDisplayId } from "../../tenant/store/TenantCommonStore";
import { dashboardWidgetDisplayId } from "../../dashboardWidget/store/DashboardWidgetCommonStore";
import { tenantService } from "../../tenant/services/tenantService";
import { dashboardWidgetService } from "../../dashboardWidget/services/dashboardWidgetService";
import { InitializeParams } from "../../../store/types";

export class TenantWidgetAccessViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = tenantWidgetAccessService;
  tenantWidgetAccessInstance: TenantWidgetAccess | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  tenantService = tenantService;
  tenantSearchValue: string = "";
  tenantIdValue: string = "";
  tenantSelectedValue: { displayId: string; id: string } | null = null;
  widgetService = dashboardWidgetService;
  widgetSearchValue: string = "";
  widgetIdValue: string = "";
  widgetSelectedValue: { displayId: string; id: string } | null = null;
  enabledValue: boolean = false;
  configOverrideValue: string = "{}";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadTenantWidgetAccessById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantWidgetAccessOne({ id });
    this.tenantWidgetAccessInstance = result.tenantWidgetAccessOne;
    if (this.tenantWidgetAccessInstance) {
      this.loadValues(this.tenantWidgetAccessInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.tenantWidgetAccessInstance) {
      return tenantWidgetAccessDisplayId(
        this.tenantWidgetAccessInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantWidgetAccessSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "tenant") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.tenantOne({ id: params.returnData.resultId });
        const tenant = result.tenantOne;
        this.tenantSelectedValue = tenant;
      } else if (params.callerData?.fieldName === "widget") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.dashboardWidgetOne({
          id: params.returnData.resultId,
        });
        const dashboardWidget = result.dashboardWidgetOne;
        this.widgetSelectedValue = dashboardWidget;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get tenantValue() {
    if (this.tenantSelectedValue) {
      return {
        id: this.tenantSelectedValue.id,
        displayId: tenantDisplayId(this.tenantSelectedValue, this.langStore),
      };
    }
    return null;
  }

  get widgetValue() {
    if (this.widgetSelectedValue) {
      return {
        id: this.widgetSelectedValue.id,
        displayId: dashboardWidgetDisplayId(
          this.widgetSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.tenantWidgetAccessInstance = null;
    this.tenantSearchValue = "";
    this.tenantIdValue = "";
    this.widgetSearchValue = "";
    this.widgetIdValue = "";
    this.enabledValue = false;
    this.configOverrideValue = "{}";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelTenantWidgetAccess() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("tenantWidgetAccess_update");
  }

  editTenantWidgetAccess() {
    const id = this.tenantWidgetAccessInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/tenantWidgetAccess/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printTenantWidgetAccess(handlePrint: () => void): void {
    handlePrint();
  }

  *closeTenantWidgetAccess(): any {
    yield this.close();
  }

  loadValues(instance: TenantWidgetAccess) {
    this.idValue = instance.id;
    this.tenantSelectedValue = instance.tenant;
    this.widgetSelectedValue = instance.widget;
    this.enabledValue = instance.enabled;
    this.configOverrideValue = instance.configOverride || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadTenantWidgetAccessById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
